import { useEffect, useState } from 'react';
import { useSite } from '../../data/SiteContext';
import { pillStyle } from './adminData';

function Skeleton() {
  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(210px,1fr))', gap:18, marginBottom:20 }}>
        {[1,2,3,4].map(i => (
          <div key={i} className="glass shimmer" style={{ padding:24, borderRadius:20 }}>
            <div style={{ height:11, width:'60%', borderRadius:6, background:'#dfeeea', marginBottom:14 }}></div>
            <div style={{ height:30, width:'45%', borderRadius:8, background:'#d2e8e3', marginBottom:10 }}></div>
            <div style={{ height:10, width:'70%', borderRadius:6, background:'#e6f2ee' }}></div>
          </div>
        ))}
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:18 }}>
        <div className="glass shimmer" style={{ padding:24, borderRadius:20 }}>
          <div style={{ height:13, width:'35%', borderRadius:6, background:'#dfeeea', marginBottom:20 }}></div>
          {[1,2,3,4,5].map(i => <div key={i} style={{ height:14, borderRadius:7, background:'#eaf4f1', marginBottom:14 }}></div>)}
        </div>
        <div className="glass shimmer" style={{ padding:24, borderRadius:20 }}>
          <div style={{ height:13, width:'50%', borderRadius:6, background:'#dfeeea', marginBottom:18 }}></div>
          <div style={{ height:130, borderRadius:14, background:'#eaf4f1' }}></div>
        </div>
      </div>
    </div>
  );
}

// "▲ 12% vs last month" / "▼ 8% vs last month" / "— no change", against the previous month.
function monthDelta(current, previous) {
  if (!previous) return current ? `▲ ${current} vs none last month` : 'No inquiries yet';
  const pct = Math.round(((current - previous) / previous) * 100);
  if (pct === 0) return '— level with last month';
  return `${pct > 0 ? '▲' : '▼'} ${Math.abs(pct)}% vs last month`;
}

const deltaColour = (text) => (text.startsWith('▼') ? '#c0492f' : text.startsWith('▲') ? '#1a7a48' : '#5a726f');

export default function Dashboard() {
  const { bookings, cycleBooking, stats, loadStats } = useSite();
  const [loading, setLoading] = useState(!stats);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    loadStats()
      .catch(e => { if (live) setError(e.message || 'Could not load dashboard figures.'); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [loadStats]);

  if (loading) return <Skeleton />;

  if (error || !stats) {
    return (
      <div className="glass" style={{ padding:'48px 24px', borderRadius:22, textAlign:'center' }}>
        <div className="serif" style={{ fontSize:23, marginBottom:8 }}>Dashboard figures unavailable</div>
        <div style={{ fontSize:14.5, color:'#5a726f' }}>{error || 'The stats endpoint returned nothing.'}</div>
      </div>
    );
  }

  const thisMonthDelta = monthDelta(stats.inquiriesThisMonth, stats.inquiriesLastMonth);
  const kpis = [
    { label:'Inquiries this month', value: stats.inquiriesThisMonth, delta: thisMonthDelta },
    { label:'Awaiting reply', value: stats.inquiriesNew, delta: `${stats.inquiriesPending} in progress · ${stats.inquiriesConfirmed} confirmed` },
    { label:'Destinations live', value: stats.destinationsPublished, delta: `of ${stats.destinations} in the catalogue` },
    { label:'Tours active', value: stats.toursActive, delta: `of ${stats.tours} created` },
  ];

  const months = stats.inquiriesPerMonth || [];
  const peak = Math.max(1, ...months.map(m => m.count));
  const top = stats.topDestinations || [];

  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(210px,1fr))', gap:18, marginBottom:20 }}>
        {kpis.map(k => (
          <div key={k.label} className="glass-card" style={{ padding:24, borderRadius:20 }}>
            <div style={{ fontSize:12.5, color:'#5a726f', fontWeight:700, letterSpacing:'.03em' }}>{k.label}</div>
            <div className="serif" style={{ fontSize:38, color:'#0e8a9c', margin:'6px 0 4px', lineHeight:1 }}>{k.value}</div>
            <div style={{ fontSize:12.5, fontWeight:800, color: deltaColour(k.delta) }}>{k.delta}</div>
          </div>
        ))}
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(300px,1fr))', gap:18 }}>
        <div className="glass-card" style={{ gridColumn:'span 2', minWidth:0, padding:24, borderRadius:22 }}>
          <div style={{ fontWeight:800, fontSize:16, marginBottom:16 }}>Recent inquiries</div>
          <div style={{ overflowX:'auto' }}>
            {bookings.length > 0 ? (
              <table className="a-table" style={{ minWidth:520 }}>
                <thead><tr><th>Ref</th><th>Client</th><th>Destination</th><th>Received</th><th>Status</th></tr></thead>
                <tbody>
                  {bookings.slice(0, 5).map(b => (
                    <tr key={b.id}>
                      <td style={{ fontWeight:800, color:'#0e8a9c' }}>{b.ref}</td>
                      <td style={{ fontWeight:600 }}>{b.name}</td>
                      <td style={{ color:'#5a726f' }}>{b.dest}</td>
                      <td>{b.date}</td>
                      <td><span onClick={() => cycleBooking(b.id)} style={{ ...pillStyle(b.status), cursor:'pointer' }}>{b.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{ padding:'34px 8px', color:'#5a726f', fontSize:14 }}>No inquiries have come in yet.</div>
            )}
          </div>
        </div>
        <div className="glass-card" style={{ padding:24, borderRadius:22 }}>
          <div style={{ fontWeight:800, fontSize:16, marginBottom:16 }}>Inquiries per month</div>
          <div style={{ display:'flex', alignItems:'flex-end', gap:7, height:130, marginBottom:8 }}>
            {months.map(m => (
              <div key={`${m.year}-${m.month}`} title={`${m.label} ${m.year}: ${m.count}`} style={{ flex:1, display:'flex', flexDirection:'column', justifyContent:'flex-end', height:'100%' }}>
                <div style={{ height: Math.max(3, (m.count / peak) * 100) + '%', borderRadius:'6px 6px 3px 3px', background:'linear-gradient(180deg,#26c6a3,#0e8a9c)', opacity: m.count ? 1 : 0.25 }}></div>
              </div>
            ))}
          </div>
          <div style={{ display:'flex', justifyContent:'space-between', fontSize:10.5, fontWeight:700, color:'#5a726f', marginBottom:20 }}>
            <span>{months[0]?.label}</span>
            <span>{months[Math.floor(months.length / 2)]?.label}</span>
            <span>{months[months.length - 1]?.label}</span>
          </div>
          <div style={{ fontWeight:800, fontSize:15, marginBottom:12 }}>Top destinations</div>
          {top.length > 0 ? top.map(d => (
            <div key={d.destinationId} style={{ marginBottom:11 }}>
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, marginBottom:5 }}>
                <span style={{ fontWeight:600 }}>{d.name}</span>
                <span style={{ color:'#5a726f', fontWeight:700 }}>{d.percent}%</span>
              </div>
              <div style={{ height:7, borderRadius:100, background:'rgba(14,138,122,0.12)', overflow:'hidden' }}>
                <div style={{ height:'100%', width:d.percent + '%', background:'linear-gradient(90deg,#26c6a3,#0e8a9c)' }}></div>
              </div>
            </div>
          )) : (
            <div style={{ fontSize:13, color:'#5a726f', lineHeight:1.6 }}>No inquiry has named a destination from your catalogue yet.</div>
          )}
        </div>
      </div>
    </div>
  );
}
